/**
 * Buildings API Client
 *
 * @remarks
 * Cliente HTTP para endpoints de building_centroids.
 */

import axios, { type AxiosInstance } from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

export interface BuildingGeoJSONParams {
  bbox?: [number, number, number, number]; // [minLng, minLat, maxLng, maxLat]
  limit?: number;
}

export interface BuildingStats {
  total: number;
  associated: number;
  notAssociated: number;
  sourceDataset: string;
}

function createApiInstance(): AxiosInstance {
  const instance = axios.create({
    baseURL: API_URL,
    headers: { 'Content-Type': 'application/json' },
  });

  instance.interceptors.request.use(
    (config) => {
      const authStore = localStorage.getItem('auth-storage');
      if (authStore) {
        try {
          const parsed = JSON.parse(authStore) as { state?: { token?: string } };
          const token = parsed.state?.token;
          if (token) config.headers.Authorization = `Bearer ${token}`;
        } catch {
          // silenciar
        }
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  return instance;
}

const api = createApiInstance();

export const buildingsApi = {
  async getGeoJSON(
    projectId: string,
    params: BuildingGeoJSONParams = {},
  ): Promise<GeoJSON.FeatureCollection> {
    const { bbox, limit = 5000 } = params;
    const queryParams: Record<string, string> = { limit: String(limit) };
    if (bbox) queryParams['bbox'] = bbox.join(',');

    const response = await api.get<GeoJSON.FeatureCollection>(
      `/projects/${projectId}/buildings/geojson`,
      { params: queryParams },
    );
    return response.data;
  },

  async getStats(projectId: string): Promise<BuildingStats> {
    const response = await api.get<BuildingStats>(
      `/projects/${projectId}/buildings/stats`,
    );
    return response.data;
  },
};
